import { Box } from '@material-ui/core';
import { LenderName, ProcessingLenderStrategyType, ProcessingMode } from '@nemuru-eng/js-api-client';
import { ChooseStrategyType } from '@nemuru-eng/js-api-client/slim';
import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { OptionListWithDetail } from '@/components/modules/MerchantDetail/MerchantLenderOrchestration/components/OptionListWithDetail';
import { OrchestrationQuestion } from '@/components/modules/MerchantDetail/MerchantLenderOrchestration/components/OrchestrationQuestion';
import { MerchantOrchestrationMap } from '@/components/modules/MerchantDetail/MerchantLenderOrchestration/maps/merchantOrchestration.map';
import { OrchestrationLabelsMap } from '@/components/modules/MerchantDetail/MerchantLenderOrchestration/maps/orchestrationLabels.map';
import { OrchestrationOptionsMap } from '@/components/modules/MerchantDetail/MerchantLenderOrchestration/maps/orchestrationOptions.map';
import {
  fromOrchestrationConfig,
  OrchestrationFormType,
  toOrchestrationConfig,
} from '@/components/modules/MerchantDetail/MerchantLenderOrchestration/schemas/orchestrationForm.schema';
import { OrchestrationEditProps } from '@/components/modules/MerchantDetail/MerchantLenderOrchestration/types/OrchestrationEditProps';
import { RankingAccess } from '@/components/modules/MerchantDetail/MerchantLenderOrchestration/types/RankingAccess';
import { CustomAlert } from '@/components/shared/components/CustomAlert';
import { FlexRow } from '@/components/shared/components/FlexRow';
import { Font } from '@/components/shared/components/Font';
import { LenderIconAvatar } from '@/components/shared/components/Logo/LenderLogo/LenderIcon';
import { SelectFormField } from '@/components/shared/components/NewContextForms/SelectFormField';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { useCustomTheme } from '@/hooks/useCustomTheme';

enum Question {
  STRATEGY = 1,
  MODE = 2,
  STOP = 3,
  RANKING = 4,
}

export const OrchestrationEditForm = ({ row, options, lenderNames, applying, onApply }: OrchestrationEditProps) => {
  const { palette, customBorderRadius } = useCustomTheme();
  const { getTextIdTranslation } = useTranslation();
  const {
    watch,
    reset,
    handleSubmit,
    formState: { isValid },
  } = useFormContext<OrchestrationFormType>();
  const [expanded, setExpanded] = useState<Question | null>(Question.STRATEGY);

  const values = watch();
  const lists = useMemo(
    () =>
      options && {
        strategy: OrchestrationOptionsMap.strategy(options),
        mode: OrchestrationOptionsMap.mode(options),
        stop: OrchestrationOptionsMap.stop(options),
        ranking: OrchestrationOptionsMap.ranking(options, values.processingStrategy),
      },
    [options, values.processingStrategy],
  );
  const rankingAccess = options ? OrchestrationOptionsMap.rankingAccess(options, row) : RankingAccess.EDITABLE;
  const isParallel = values.processingStrategy === ProcessingLenderStrategyType.PARALLEL;
  const defaultConfig = MerchantOrchestrationMap.defaultConfigFor(row.paymentMethodType);
  const agentsCount = row.groups.flatMap((group) => group.agents).length;
  const priorityLenderNames =
    values.lenderName && !lenderNames.includes(values.lenderName as LenderName)
      ? [...lenderNames, values.lenderName]
      : lenderNames;

  const toggle = (question: Question) => setExpanded((current) => (current === question ? null : question));
  const submit = handleSubmit(async (formValues) => {
    try {
      await onApply(toOrchestrationConfig(formValues));
    } catch {
      // useRequest already shows the rejected snackbar; the panel stays open to retry.
    }
  });

  if (!lists || rankingAccess === RankingAccess.READ_ONLY) {
    return (
      <Box bgcolor={palette.grey[100]} borderRadius={customBorderRadius.paper} p={3} my={1}>
        <CustomAlert
          severity="info"
          title="LENDER_ORCHESTRATION_READ_ONLY_TITLE"
          description="LENDER_ORCHESTRATION_READ_ONLY_TEXT"
        />
      </Box>
    );
  }

  return (
    <Box
      bgcolor={palette.common.white}
      border={`1px solid ${palette.grey[200]}`}
      borderRadius={customBorderRadius.paper}
      px={3}
      pt={1}
      pb={3}
      my={1}
    >
      <OrchestrationQuestion
        index={Question.STRATEGY}
        titleTextId="LENDER_ORCHESTRATION_COLUMN_STRATEGY"
        questionTextId="LENDER_ORCHESTRATION_QUESTION_STRATEGY"
        valueTextId={OrchestrationLabelsMap.strategy(values.processingStrategy)}
        expanded={expanded === Question.STRATEGY}
        onToggle={() => toggle(Question.STRATEGY)}
      >
        <OptionListWithDetail
          name="processingStrategy"
          labelTextId="LENDER_ORCHESTRATION_COLUMN_STRATEGY"
          options={lists.strategy}
          value={values.processingStrategy}
        />
      </OrchestrationQuestion>
      <OrchestrationQuestion
        index={Question.MODE}
        titleTextId="LENDER_ORCHESTRATION_COLUMN_MODE"
        questionTextId="LENDER_ORCHESTRATION_QUESTION_MODE"
        valueTextId={OrchestrationLabelsMap.mode(values.processingMode)}
        expanded={expanded === Question.MODE}
        onToggle={() => toggle(Question.MODE)}
        disabled={isParallel}
      >
        <OptionListWithDetail
          name="processingMode"
          labelTextId="LENDER_ORCHESTRATION_COLUMN_MODE"
          options={lists.mode}
          value={values.processingMode}
        />
      </OrchestrationQuestion>
      <OrchestrationQuestion
        index={Question.STOP}
        titleTextId="LENDER_ORCHESTRATION_COLUMN_STOP"
        questionTextId="LENDER_ORCHESTRATION_QUESTION_STOP"
        valueTextId={OrchestrationLabelsMap.stopCondition(values.stopCondition)}
        expanded={expanded === Question.STOP}
        onToggle={() => toggle(Question.STOP)}
        disabled={isParallel}
      >
        <OptionListWithDetail
          name="stopCondition"
          labelTextId="LENDER_ORCHESTRATION_COLUMN_STOP"
          options={lists.stop}
          value={values.stopCondition}
        />
      </OrchestrationQuestion>
      <OrchestrationQuestion
        index={Question.RANKING}
        titleTextId="LENDER_ORCHESTRATION_COLUMN_RANKING"
        questionTextId="LENDER_ORCHESTRATION_QUESTION_RANKING"
        valueTextId={OrchestrationLabelsMap.chooseStrategy(values.chooseStrategyType)}
        expanded={expanded === Question.RANKING}
        onToggle={() => toggle(Question.RANKING)}
      >
        {rankingAccess === RankingAccess.LOCKED ? (
          <CustomAlert
            severity="info"
            title="LENDER_ORCHESTRATION_LOCKED_TITLE"
            description="LENDER_ORCHESTRATION_LOCKED_TEXT"
          />
        ) : (
          <OptionListWithDetail
            name="chooseStrategyType"
            labelTextId="LENDER_ORCHESTRATION_COLUMN_RANKING"
            options={lists.ranking}
            value={values.chooseStrategyType}
          >
            {values.chooseStrategyType === ChooseStrategyType.PRIORITY_LENDER_THEN_PRICE && (
              <Box mt={5}>
                <SelectFormField
                  name="lenderName"
                  label="LENDER_ORCHESTRATION_PRIORITY_LENDER"
                  tooltipTitle="LENDER_ORCHESTRATION_PRIORITY_LENDER_TEXT"
                  fieldVariant={FieldVariant.form}
                  selectOptions={priorityLenderNames.map((lenderName) => ({
                    value: lenderName,
                    display: (
                      <FlexRow justifyContent="flex-start" gridGap={8}>
                        <LenderIconAvatar size="small" lenderName={lenderName as LenderName} />
                        <Text tId={lenderName} />
                      </FlexRow>
                    ),
                  }))}
                />
              </Box>
            )}
          </OptionListWithDetail>
        )}
      </OrchestrationQuestion>

      {!isParallel && values.processingMode === ProcessingMode.MANUAL && (
        <Box mt={2}>
          <CustomAlert
            severity="warning"
            title="LENDER_ORCHESTRATION_MANUAL_WARNING_TITLE"
            description="LENDER_ORCHESTRATION_MANUAL_WARNING_TEXT"
          />
        </Box>
      )}

      <FlexRow mt={3} gridGap={16} alignItems="center">
        <Box flexGrow={1}>
          <Font variant="caption" color="textSecondary">
            <Text
              tId="LENDER_ORCHESTRATION_DEFAULT_HINT"
              dynamicDataTexts={[
                getTextIdTranslation(OrchestrationLabelsMap.strategy(defaultConfig.processingStrategy)),
                getTextIdTranslation(OrchestrationLabelsMap.chooseStrategy(defaultConfig.chooseStrategyType)),
              ]}
            />
          </Font>
        </Box>
        <StandardButton
          size="small"
          onClick={() => reset(fromOrchestrationConfig(defaultConfig))}
          disabled={applying || rankingAccess === RankingAccess.LOCKED}
        >
          <Font variant="body2">
            <Text tId="LENDER_ORCHESTRATION_RESET_DEFAULT" />
          </Font>
        </StandardButton>
        <StandardButton main size="small" loading={applying} disabled={!isValid} onClick={submit}>
          <Font variant="body2">
            <Text
              tId={agentsCount === 1 ? 'LENDER_ORCHESTRATION_APPLY_ONE' : 'LENDER_ORCHESTRATION_APPLY'}
              dynamicDataTexts={[String(agentsCount)]}
            />
          </Font>
        </StandardButton>
      </FlexRow>
    </Box>
  );
};
